import React, { useCallback } from 'react';
import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  Tooltip,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { Editor } from '@tiptap/react';

import {
  InsertTable,
  DeleteTable,
  RowBelow,
  ColumnRight,
} from '@/assets/icons/table';

interface TableMenuProps {
  editor: Editor;
}

export const TableMenu: React.FC<TableMenuProps> = ({ editor }) => {
  const addRowBefore = useCallback(() => {
    editor?.chain().focus().addRowBefore().run();
  }, [editor]);

  const addRowAfter = useCallback(() => {
    editor?.chain().focus().addRowAfter().run();
  }, [editor]);

  const deleteRow = useCallback(() => {
    editor?.chain().focus().deleteRow().run();
  }, [editor]);

  const addColumnBefore = useCallback(() => {
    editor?.chain().focus().addColumnBefore().run();
  }, [editor]);

  const addColumnAfter = useCallback(() => {
    editor?.chain().focus().addColumnAfter().run();
  }, [editor]);

  const deleteColumn = useCallback(() => {
    editor?.chain().focus().deleteColumn().run();
  }, [editor]);

  const deleteTable = useCallback(() => {
    editor?.chain().focus().deleteTable().run();
  }, [editor]);

  // const mergeCells = useCallback(() => {
  //   editor?.chain().focus().mergeCells().run();
  // }, [editor]);

  // const splitCell = useCallback(() => {
  //   editor?.chain().focus().splitCell().run();
  // }, [editor]);

  const toggleHeaderRow = useCallback(() => {
    editor?.chain().focus().toggleHeaderRow().run();
  }, [editor]);

  const toggleHeaderColumn = useCallback(() => {
    editor?.chain().focus().toggleHeaderColumn().run();
  }, [editor]);

  return (
    <Flex
      className="table-floating-menu"
      bg="white"
      borderRadius="8px"
      boxShadow="0 2px 12px rgba(0, 0, 0, 0.12)"
      border="1px solid"
      borderColor="gray.200"
      p="4px"
      gap="2px"
      alignItems="center"
    >
      {/* Row Actions */}
      <Menu placement="bottom-start">
        <Tooltip label="Row actions" placement="top" hasArrow openDelay={300}>
          <MenuButton
            as={IconButton}
            icon={<Icon as={RowBelow as any} boxSize="16px" />}
            size="sm"
            variant="ghost"
            aria-label="Row actions"
            _hover={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList zIndex={1000} minW="180px" fontSize="14px">
          <MenuItem
            // icon={<Icon as={RowAbove as any} boxSize="16px" />}
            onClick={addRowBefore}
          >
            Insert row above
          </MenuItem>
          <MenuItem
            // icon={<Icon as={RowBelow as any} boxSize="16px" />}
            onClick={addRowAfter}
          >
            Insert row below
          </MenuItem>
          <MenuDivider />
          <MenuItem
            // icon={
            //   <Icon as={RiDeleteRow as any} boxSize="16px" color="red.500" />
            // }
            onClick={deleteRow}
            color="red.500"
          >
            Delete row
          </MenuItem>
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="20px" borderColor="gray.300" />

      {/* Column Actions */}
      <Menu placement="bottom-start">
        <Tooltip
          label="Column actions"
          placement="top"
          hasArrow
          openDelay={300}
        >
          <MenuButton
            as={IconButton}
            icon={<Icon as={ColumnRight as any} boxSize="16px" />}
            size="sm"
            variant="ghost"
            aria-label="Column actions"
            _hover={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList zIndex={1000} minW="180px" fontSize="14px">
          <MenuItem
            // icon={<Icon as={ColumnLeft as any} boxSize="16px" />}
            onClick={addColumnBefore}
          >
            Insert column left
          </MenuItem>
          <MenuItem
            // icon={<Icon as={ColumnRight as any} boxSize="16px" />}
            onClick={addColumnAfter}
          >
            Insert column right
          </MenuItem>
          <MenuDivider />
          <MenuItem
            // icon={
            //   <Icon as={RiDeleteColumn as any} boxSize="16px" color="red.500" />
            // }
            onClick={deleteColumn}
            color="red.500"
          >
            Delete column
          </MenuItem>
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="20px" borderColor="gray.300" />

      {/* Merge/Split Cells (disabled for now, if needed in future we can enable it) */}
      {/* <Tooltip label="Merge cells" placement="top" hasArrow openDelay={300}>
        <IconButton
          icon={<Icon as={RiMergeCellsHorizontal as any} boxSize="16px" />}
          size="sm"
          variant="ghost"
          aria-label="Merge cells"
          onClick={mergeCells}
          isDisabled={!editor?.can().mergeCells()}
          _hover={{ bg: 'gray.100' }}
          _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
        />
      </Tooltip>

      <Tooltip label="Split cell" placement="top" hasArrow openDelay={300}>
        <IconButton
          icon={<Icon as={RiSplitCellsHorizontal as any} boxSize="16px" />}
          size="sm"
          variant="ghost"
          aria-label="Split cell"
          onClick={splitCell}
          isDisabled={!editor?.can().splitCell()}
          _hover={{ bg: 'gray.100' }}
          _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
        />
      </Tooltip> */}

      {/* <Divider orientation="vertical" h="20px" borderColor="gray.300" /> */}

      {/* Header Options */}
      <Menu placement="bottom-start">
        <Tooltip
          label="Header options"
          placement="top"
          hasArrow
          openDelay={300}
        >
          <MenuButton
            as={IconButton}
            icon={<Icon as={InsertTable as any} boxSize="16px" />}
            size="sm"
            variant="ghost"
            aria-label="Header options"
            _hover={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList zIndex={1000} minW="180px" fontSize="14px">
          <MenuItem onClick={toggleHeaderRow}>Toggle header row</MenuItem>
          <MenuItem onClick={toggleHeaderColumn}>Toggle header column</MenuItem>
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="20px" borderColor="gray.300" />

      {/* Delete Table */}
      <Tooltip label="Delete table" placement="top" hasArrow openDelay={300}>
        <IconButton
          icon={<Icon as={DeleteTable as any} boxSize="16px" />}
          size="sm"
          variant="ghost"
          aria-label="Delete table"
          onClick={deleteTable}
          color="red.500"
          _hover={{ bg: 'red.50' }}
        />
      </Tooltip>
    </Flex>
  );
};

export default TableMenu;
